/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React from 'react';

import { useIntl } from 'react-intl';
import { graphql, useMutation } from 'react-relay';

import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import { SplitButton } from '@atlaskit/button/split-button/split-button';
import { cssMap, jsx } from '@atlaskit/css';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import { useFlags } from '@atlaskit/flag/use-flags';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import StatusErrorIcon from '@atlaskit/icon/core/cross-circle';
import EyeOpenIcon from '@atlaskit/icon/core/eye-open';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { FinishMigrationReviewModal } from '@atlassian/company-hub-common/finish-migration-review-modal';
import { getMigrationPublishedUrl } from '@atlassian/company-hub-common/migration-publish-success';
import { PublishMigrationModal } from '@atlassian/company-hub-common/publish-migration-modal';
import type { MigrationReview } from '@atlassian/company-hub-common/types';

import type { migrationReviewActionBarGoLiveHelpCenterMutation } from './__generated__/migrationReviewActionBarGoLiveHelpCenterMutation.graphql';
import messages from './messages';

const styles = cssMap({
	container: {
		paddingBlockEnd: token('space.150'),
		paddingBlockStart: token('space.150'),
		paddingInlineEnd: token('space.300'),
		paddingInlineStart: token('space.300'),
	},
});

export const MigrationReviewActionBar = ({
	cloudId,
	helpCenterAri,
	helpCenterName,
	migrationReview,
	publishedHelpCenterUrl,
}: {
	cloudId: string;
	helpCenterAri: string;
	helpCenterName: string;
	migrationReview: MigrationReview;
	publishedHelpCenterUrl: string;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { showFlag } = useFlags();
	const [isFinishLaterModalOpen, setIsFinishLaterModalOpen] = React.useState(false);
	const [isPublishModalOpen, setIsPublishModalOpen] = React.useState(false);
	const [commitGoLiveHelpCenter, isPublishing] =
		useMutation<migrationReviewActionBarGoLiveHelpCenterMutation>(graphql`
			mutation migrationReviewActionBarGoLiveHelpCenterMutation(
				$cloudId: ID!
				$input: HelpCenterHubGoLiveHelpCenterInput!
			) {
				helpCenter(cloudId: $cloudId) @optIn(to: "HelpCenterAggBeta") {
					goLiveHelpCenter(input: $input) {
						success
						errors {
							message
						}
					}
				}
			}
		`);

	const showPublishErrorFlag = React.useCallback(() => {
		const title = formatMessage(messages.publishError);
		showFlag({
			title,
			isAutoDismiss: true,
			icon: <StatusErrorIcon label={title} color={token('color.icon.danger')} spacing="spacious" />,
		});
	}, [formatMessage, showFlag]);

	const handlePublishModalOpen = React.useCallback(() => {
		setIsPublishModalOpen(true);
	}, []);

	const handlePublishModalCancel = React.useCallback(() => {
		setIsPublishModalOpen(false);
	}, []);

	const handleFinishLaterModalOpen = React.useCallback(() => {
		setIsFinishLaterModalOpen(true);
	}, []);

	const handleFinishLaterModalCancel = React.useCallback(() => {
		setIsFinishLaterModalOpen(false);
	}, []);

	const handlePublish = () => {
		if (isPublishing) {
			return;
		}
		setIsPublishModalOpen(false);

		commitGoLiveHelpCenter({
			variables: {
				cloudId,
				input: { helpCenterAri },
			},
			onCompleted: (response) => {
				if (response.helpCenter?.goLiveHelpCenter?.success) {
					window.location.href = getMigrationPublishedUrl(publishedHelpCenterUrl);
					return;
				}

				showPublishErrorFlag();
			},
			onError: showPublishErrorFlag,
		});
	};

	const handleFinishLaterConfirm = () => {
		migrationReview.onFinishLater();
		setIsFinishLaterModalOpen(false);
	};

	return (
		<React.Fragment>
			<Box backgroundColor="color.background.accent.blue.subtlest" xcss={styles.container}>
				<Inline
					alignBlock="center"
					rowSpace="space.150"
					shouldWrap
					space="space.200"
					spread="space-between"
					testId="migration-review-action-bar"
				>
					<Inline alignBlock="center" space="space.100">
						<EyeOpenIcon label="" />
						<Text>{formatMessage(messages.banner)}</Text>
					</Inline>
					<Inline
						alignBlock="center"
						rowSpace="space.100"
						shouldWrap
						space="space.100"
						testId="migration-review-action-bar-actions"
					>
						<SplitButton appearance="primary">
							<Button
								isDisabled={isPublishing}
								isLoading={isPublishing}
								onClick={handlePublishModalOpen}
							>
								{formatMessage(messages.publish)}
							</Button>
							<DropdownMenu<HTMLButtonElement>
								shouldRenderToParent
								trigger={({ triggerRef, ...triggerProps }) => (
									<IconButton
										ref={triggerRef}
										{...triggerProps}
										icon={ChevronDownIcon}
										label={formatMessage(messages.morePublishActions)}
									/>
								)}
							>
								<DropdownItemGroup>
									<DropdownItem onClick={migrationReview.onEditDraft}>
										{formatMessage(messages.editDraft)}
									</DropdownItem>
									<DropdownItem onClick={handleFinishLaterModalOpen}>
										{formatMessage(messages.finishLater)}
									</DropdownItem>
								</DropdownItemGroup>
							</DropdownMenu>
						</SplitButton>
					</Inline>
				</Inline>
			</Box>
			<PublishMigrationModal
				isOpen={isPublishModalOpen}
				onCancel={handlePublishModalCancel}
				onConfirm={handlePublish}
			/>
			<FinishMigrationReviewModal
				helpCenterName={helpCenterName}
				isOpen={isFinishLaterModalOpen}
				onCancel={handleFinishLaterModalCancel}
				onConfirm={handleFinishLaterConfirm}
			/>
		</React.Fragment>
	);
};
